/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import { claudeCliLink, codexOpenLink, claudePluginCommand, codexPluginCommand } from './links';
import type { ClientOverride, McpClientId, McpServerDescriptor } from './types';

export const KNOWN_MARKETPLACES: Record<string, { source: string; guideBase: string }> = {
  'm42-platform': {
    source: 'm42-platform/claude-m42-platform',
    guideBase: 'https://github.com/m42-platform/claude-m42-platform/tree/main/plugins/',
  },
};

export const BACKSTAGE_URL = 'https://backstage.infra.mission42.com/api/mcp-actions/v1/backstage-m42';
export const OVERRIDE_TARGETS: Record<string, { url: string; plugin?: string }> = {
  'backstage-m42': { url: BACKSTAGE_URL, plugin: 'backstage-m42@m42-platform' },
};
const BACKSTAGE_PLUGIN = {
  id: 'backstage-m42@m42-platform',
  source: KNOWN_MARKETPLACES['m42-platform'].source,
  guideUrl: `${KNOWN_MARKETPLACES['m42-platform'].guideBase}backstage-m42`,
};
const MCP_DOC = {
  label: 'MCP connector guide',
  href: 'https://github.com/m42-platform/infra-svc-backstage/blob/main/docs/mcp-oauth.md',
};

/** Claude Desktop's documented deep link opens a NEW CHAT with `q` pre-filled and
 * never sent (support.claude.com article 14729294; `q` caps around 14,000 chars).
 * It cannot install or authorize anything itself. What makes it a connect button is
 * that Claude Desktop MATCHES THE PROMPT TEXT against the org's connectors and puts
 * a "Connectors that could help → backstage [Connect]" card above its reply; that
 * Connect button runs the OAuth flow. Verified 2026-09-08 from a disconnected
 * account: the card appeared, and Claude's own reply confirmed why nothing else
 * could work — the connector was installed org-wide but `enabledInChat: false`, so
 * it had no tools to call and therefore no 401 to trigger a sign-in ("the sign-in
 * flow you're expecting is triggered by the first tool call, and I can't make one").
 * So the prompt must name Backstage plainly enough to match, and must tell the user
 * to click that card instead of letting Claude narrate the diagnosis — which is what
 * it did unprompted, at length.
 *
 * It asks for ONE entity by name, routing to `get-catalog-entity`. The obvious
 * phrasing ("list a few services") routes to `query-catalog-entities`, whose
 * predicate schema rejects a plain field key beside an operator and then blames the
 * field, observed sending an authorized Claude Desktop into ~36s of retries on first
 * contact. Bad first impression for exactly these users. */
const CLAUDE_DESKTOP_CONNECT_LINK =
  'claude://claude.ai/new?surface=chat&q=' +
  encodeURIComponent(
    'Connect to m42 Backstage — use the backstage connector to show me who owns ' +
      'svc-user and what it depends on. If the connector is not enabled in this ' +
      'chat yet, reply with one line telling me to click Connect on the connector ' +
      'card, and nothing else.',
  );

/** Copy for servers verified in depth, keyed by install name. */
export const SERVER_OVERRIDES: Record<string, Partial<Record<McpClientId, ClientOverride>>> = {
  'backstage-m42': {
    'claude-code': {
      body: 'The backstage-m42 plugin adds the connector plus analysis skills and agents built for this instance. Install once, then just ask.',
      copiedLine: '✓ copied — analysis skills · agents · MCP included',
      connect: {
        label: '1-Click Connect',
        href: claudeCliLink(`Install the backstage-m42 plugin for this Backstage instance by running:\n${claudePluginCommand(BACKSTAGE_PLUGIN)}`),
        hint: 'Prefills the command in a new Claude Code session — press Enter, approve it, then /mcp to sign in.',
      },
      variants: {
        'mcp-only': {
          body: 'No marketplace access needed — the connector on its own, without the analysis skills and agents. Pick this if the plugin install fails, or you only want the tools.',
        },
      },
    },
    'claude-desktop': {
      body: 'Claude Desktop and claude.ai share one Connectors list, and m42 should already be on it org-wide. Open Customize → Connectors and click Connect on the entry whose URL matches the one shown here.',
      connect: {
        label: '1-Click Connect',
        href: CLAUDE_DESKTOP_CONNECT_LINK,
        hint: 'Prefills a prompt in Claude Desktop — press Enter, then Connect on the card above its reply.',
      },
      note: 'Not listed, or 1-Click Connect shows no card? Then nobody has added it yet: on a personal plan use “Add custom connector”; on Team or Enterprise only an Owner can, choosing “No client ID — register one automatically”. Sign-ins last about a day — it never refreshes.',
    },
    opencode: {
      note: 'Sessions last weeks. OpenCode reads the scopes the server advertises and asks for a refresh token itself, so the oauth.scope line this used to need is gone — delete it if you still have one.',
    },
    codex: {
      body: 'Codex installs the same backstage-m42 plugin — the connector plus its analysis skills. Install, then sign in with codex mcp login backstage-m42.',
      connect: {
        label: '1-Click Connect',
        href: codexOpenLink(`Install the backstage-m42 plugin for this Backstage instance by running:\n${codexPluginCommand(BACKSTAGE_PLUGIN)} && codex mcp login backstage-m42`),
        hint: 'Prefills the command in a new Codex chat — press Enter, approve it, then click the sign-in link Codex posts.',
      },
    },
  },
};

export const BACKSTAGE_M42: McpServerDescriptor = {
  name: 'backstage-m42',
  title: 'm42 Backstage',
  transport: { kind: 'remote', url: BACKSTAGE_URL },
  clients: ['claude-code', 'claude-desktop', 'cursor', 'opencode', 'codex'],
  plugin: BACKSTAGE_PLUGIN,
  docLink: MCP_DOC,
  overrides: SERVER_OVERRIDES['backstage-m42'],
};
